import { useState, useEffect } from "react";
import { bindActionCreators, Dispatch } from "redux";
import { connect, RootStateOrAny } from "react-redux";
import { useHistory } from "react-router";
import moment from 'moment';
import * as XLSX from 'xlsx';
import _ from "lodash";

import { Breadcrumb, Button, Card, Table, Typography } from "antd";
import { CloudDownloadOutlined, HomeOutlined, SearchOutlined, UserOutlined } from "@ant-design/icons";

import { ErrorResponse, UserModel } from "utils/types";
import { formatDate, handleDownload } from "utils/helpers";
import notifier from "utils/notifier";
import { Company, DocumentCategory } from "utils/enums";

import { getExpired } from "store/authorization/expired/actions";
import { getUnusedAuths } from "store/authorization/unused/actions";
import { getHelpCenterLinks } from 'store/app/helpCenter/actions';

//eligibility info page related
import { SearchCriteria, search } from "store/eligibility/actions";
import { downloadMemberDocument } from "store/eligibility/documents/actions";
import { updateReadInfo } from "store/eligibility/pac/actions";
import { getAuthEnabledProviderList, GetAuthEnabledProviderListResponse } from 'store/authorization/provider/actions';

import HelpCenterLink from 'components/Common/HelpCenterLink';
import OpenAuthForm from "components/Documents/OpenAuth";
import AuthDetail from 'components/Auth/Detail';

import styles from './OpenAuth.module.scss';

const { Title } = Typography;

interface OpenAuthPageProps {
  getExpired(userId: string): any;
  getUnusedAuths(userId: string, rptType: string, provKeyId: string): any;
  downloadMemberDocument(memberFilePath: string, memberFileName: string, membMpiNo: string): any;
  updateReadInfo(passedUserId: string, passedPacUid: string, passedPacType: string): any;
  getAuthEnabledProviderList(userModel: UserModel): any;
  isFetchingExpired: boolean;
  isFetchingUnused: boolean;

  //eligibility info page related
  search(searchCriteria: SearchCriteria): any;

  getHelpCenterLinks(companyId: string, page: string, type: string): any;
}

const OpenAuthPage = (props: OpenAuthPageProps) => {

  const history = useHistory();
  const [openAuthDataSource, setOpenAuthDataSource] = useState<Object[]>([]);
  const [unusedAuthDataSource, setUnusedAuthDataSource] = useState<any[]>([]);
  const [providerList, setProviderList] = useState([] as any);
  const [authDetail, setAuthDetail] = useState({});
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedRptType, setSelectedRptType] = useState('');
  const [state, setState] = useState('start');
  const [helpCenter, setHelpCenter] = useState<any>([]);

  const authEnabledProviderListParam = {
    UserId: sessionStorage.user_id,
    UserRole: sessionStorage.user_role
  };

  useEffect(() => {
    //getExpired();
    getHelpCenterLinks();
  }, []);


  const getHelpCenterLinks = () => {
    props.getHelpCenterLinks(sessionStorage.getItem('ipaGroup')!, '/auth/open-auth', 'page')
      .then((response: any) => {
        setHelpCenter(response);
    })
    .catch((err: ErrorResponse) => {
      console.log(err?.response?.data?.Message);
    });
  }
  //Update Read Info API after you click on download member document link
  const updateReadInfo = (document: any, pacType: string) => {
    return props.updateReadInfo(sessionStorage.user_id, document.RmiUid, pacType)
      .then(() => {
        getExpired();
      })
      .catch((err: ErrorResponse) =>
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      );
  }

  //Download Documents API when you click on the links on the table
  //document too long to define
  const downloadMemberDocument = (documentFile: any, pacType: string) => {
    return props.downloadMemberDocument(documentFile.RmiFilePath, documentFile.RmiFileName, documentFile.RmiUid)
      .then((response: any) => {
        handleDownload(response, documentFile.RmiFileName);
        updateReadInfo(documentFile, pacType);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.')
      });
  }

  const triggerAuthDetailPage = (data: any) => {
    setState('auth-detail');
    setAuthDetail(data);
  }

  const triggerStartPage = () => {
    setState('start');
  }

  const openAuthAndMemberRosterColumns = [
    {
      title: "IPA",
      key: "RmiCompanyId",
      dataIndex: "RmiCompanyId",
      render: (text: string) => getDisplayCompanyId(text),
      sorter: (a: any, b: any) => a.RmiCompanyId.localeCompare(b.RmiCompanyId),
    },
    {
      title: "Provider Name",
      key: "RmiProvName",
      dataIndex: "RmiProvName",
      sorter: (a: any, b: any) => a.RmiProvName.localeCompare(b.RmiProvName),
    },
    {
      title: "File Name",
      key: "RmiOrigFileName",
      dataIndex: "RmiOrigFileName",
      render: (_: string, record: any) => (
        <a onClick={() => downloadMemberDocument(record, DocumentCategory.OPEN_AUTHS)}>
          {record.RmiOrigFileName}
        </a>
      ),
      sorter: (a: any, b: any) => a.RmiOrigFileName.localeCompare(b.RmiOrigFileName),
    },
    {
      title: "Upload Date",
      key: "RmiUploadDate",
      dataIndex: "RmiUploadDate",
      render: (_: string, record: any) => formatDate(record.RmiUploadDate, "MM/DD/YYYY [at] h:mmA"),
      sorter: (a: any, b: any) => a.RmiUploadDate.localeCompare(b.RmiUploadDate),
    },
    {
      title: "Read By",
      key: "RmiReadBy",
      dataIndex: "RmiReadBy",
      sorter: (a: any, b: any) => {
        a = a.RmiReadBy || '';
        b = b.RmiReadBy || '';
        return a.localeCompare(b);
      },
    },
    {
      title: "Read Date",
      key: "RmiReadDate",
      dataIndex: "RmiReadDate",
      render: (_: string, record: any) => formatDate(record.RmiReadDate, "MM/DD/YYYY [at] h:mmA"),
      sorter: (a: any, b: any) =>  {
        a = a.RmiReadDate || '';
        b = b.RmiReadDate || '';
        return a.localeCompare(b);
      },
    },
  ];

  const unusedAuthColumns = [
    {
      title: "Auth Number",
      key: "AuthNo",
      dataIndex: "AuthNo",
      render: (_: string, record: any) => {
        return <><a href="#/" onClick={() => triggerAuthDetailPage({companyId: record.Ipa, authNo: record.AuthNo})}>{record.AuthNo}</a></>
      },
      sorter: (a: any, b: any) => a.AuthNo.localeCompare(b.AuthNo),
    },
    {
      title: "Member Name",
      key: "MemberName",
      dataIndex: "MemberName",
      sorter: (a: any, b: any) => a.MemberName.localeCompare(b.MemberName),
    },
    {
      title: "Referred By",
      key: "ReferByName",
      dataIndex: "ReferByName",
      sorter: (a: any, b: any) => a.ReferByName.localeCompare(b.ReferByName),
    },
    {
      title: "Referred To",
      key: "ReferToName",
      dataIndex: "ReferToName",
      sorter: (a: any, b: any) => {
        a = a.ReferToName || '';
        b = b.ReferToName || '';
        return a.localeCompare(b);
      },
    },
    {
      title: "Action Date",
      key: "ActionDate",
      dataIndex: "ActionDate",
      render: (text: string) => text ? moment(text).format('MM/DD/YYYY') : null,
      sorter: (a: any, b: any) => moment(a.ActionDate, 'MM/DD/YYYY hh:mm:ss A').unix() - moment(b.ActionDate, 'MM/DD/YYYY hh:mm:ss A').unix(),
    },
    {
      title: "Expiration Date",
      key: "ExpiryDate",
      dataIndex: "ExpiryDate",
      render: (_: string, record: any) => formatDate(record.ExpiryDate, "MM/DD/YYYY"),
      sorter: (a: any, b: any) =>  {
        a = formatDate(a.ExpiryDate, "MM/DD/YYYY");
        b = formatDate(b.ExpiryDate, "MM/DD/YYYY");
        return a.localeCompare(b);
      },
    },
    {
      title: "Member PCP",
      key: "MemberPcpName",
      dataIndex: "MemberPcpName",
      sorter: (a: any, b: any) => {
        a = a.MemberPcpName || '';
        b = b.MemberPcpName || '';
        return a.localeCompare(b);
      },
    },
  ];

  const getDisplayCompanyId = (companyId: string) => {
    if (companyId === Company.CVIPA) {
      companyId = Company.EHIPA;
    }
    return companyId;
  };

  //open auth table API
  const getExpired = () => {
    return props
      .getExpired(sessionStorage.user_id)
      .then((response: any) => {
        setOpenAuthDataSource(_.orderBy(response, "RmiUploadDate", "desc"));
        // setShowTable(response.length > 0 ? true : false);
        // getUnreadCount();
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response?.data?.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  const ExportToExcel = () => {
    const columnsToHide = ['attach', 'authNum', 'key'];
    const columnMapping: Record<string, string> = {
      Ipa: 'IPA',
      AuthNo: 'Auth Number',
      MemberName: 'Member Name',
      ReferByName: 'Refer By Provider',
      ReferToName: 'Refer To Provider',
      Diags: 'Diags',
      CptCodes: 'CPT Codes',
      ActionDate: 'Action Date',
      ExpiryDate: 'Expiry Date',
      MemberPcpName: 'Member PCP Name'
    };
    const columnOrder: string[] = ['IPA', 'Auth Number', 'Member Name', 'Refer By Provider', 'Refer To Provider', 'Diags', 'CPT Codes', 'Action Date', 'Expiry Date', 'Member PCP Name'];

    const exportToExcel = () => {
      const filteredData = unusedAuthDataSource.map(item => {
        const newItem: Record<string, any> = {};
        columnOrder.forEach(column => {
          const originalColumn = Object.keys(columnMapping).find(key => columnMapping[key] === column) || column;
          if (item[originalColumn] !== undefined && !columnsToHide.includes(originalColumn)) {
            newItem[column] = item[originalColumn];
          }
        });
        return newItem;
      });

      const worksheet = XLSX.utils.json_to_sheet(filteredData);
      const workbook = XLSX.utils.book_new();
      let fileName = selectedRptType === 'PCP' ? 'UnusedAuth' : 'OpenAuth';
      fileName = `${fileName}_${sessionStorage.user_id}_${selectedRptType}_${selectedProvider}_${moment().format("MMDDYYYY")}.xlsx`;
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      XLSX.writeFile(workbook, fileName);
    };

    return (
      <Button
        type="primary"
        icon={<CloudDownloadOutlined />}
        onClick={exportToExcel}
      >
        Export to Excel
      </Button>
    );
  };

  return (
    <div className="documents-page">
      <div className="header-title" style={{ marginTop: '20px' }}>
        <Title level={4} className="ml-3">Open Authorizations</Title>
      </div>
      <div>
        <Breadcrumb>
          <Breadcrumb.Item>
            <HomeOutlined
             onClick={() => history.push('/quality/overview')}
            />
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <SearchOutlined />
              <a href="#/" onClick={triggerStartPage}>Search</a>
          </Breadcrumb.Item>
          {
            state === 'auth-detail' &&
            <Breadcrumb.Item >
            <UserOutlined />
              <a href="#/" onClick={triggerAuthDetailPage}>Authorization Details</a>
            </Breadcrumb.Item>
          }
        </Breadcrumb>
        {
          helpCenter?.length > 0 ? helpCenter.map((hc: any) => {
            return (
              <>
                <div className={`row ${styles['help-center-link']}`}>
                  <div className="col-md-24">
                    <HelpCenterLink
                      clickEventId={hc.ClickEventId}
                      linkName={hc.LinkName}
                      pagePath={hc.PagePath}
                      url={hc.Link}
                      companyId={hc.CompanyId}
                    />
                  </div>
                </div>
              </>
            );
          }) : null
        }
      </div>

      {state === 'start' && (
        <div>
          <div>
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr>
                  <td colSpan={3}>
                    <Card className="row justify-content-center text-align-center">
                      <p><b>Below list of members have been referred to your office or your office has submitted
                        a request for services. We do not show that the services have yet been rendered
                        on these referrals that are more than 30 days old.</b>
                      </p>
                    </Card>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Card className="row justify-content-center text-align-center">
                      <p><b>Please follow up with the member to determine if this referral
                        is still required or not and document in your notes.</b>
                      </p>
                    </Card>
                  </td>
                  <td>
                    <Card className="row justify-content-center text-align-center">
                      <p><b>If you have seen the member for a consult and or follow up, please
                        upload your notes to the portal for the pcp to view or fax them to their office.</b>
                      </p>
                    </Card>
                  </td>
                  <td>
                    <Card className="row justify-content-center text-align-center">
                      <p><b>Health Plans require that there is communication between the PCP and SCP,
                        in addition to ensure the member is following up is required.</b>
                      </p>
                    </Card>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="search-section" style={{ marginLeft: '10px' }}>
            <OpenAuthForm
              isFetchingUnused={props.isFetchingUnused}
              getUnusedAuths={props.getUnusedAuths}
              getAuthEnabledProviderList={props.getAuthEnabledProviderList}
              setUnusedAuthDataSource={setUnusedAuthDataSource}
              setSelectedProvId={setSelectedProvider}
              setSelectedRptType={setSelectedRptType}
              setProviderList={setProviderList}
              providerList={providerList}
            />
          </div>
          {unusedAuthDataSource && unusedAuthDataSource.length !== 0 && (
            <div className="content">
              <div className={styles['header']}>
                <Title level={4}>Authorizations</Title>
                <div className="export-button">
                  <ExportToExcel />
                </div>
              </div>
              <Table
                columns={unusedAuthColumns}
                dataSource={unusedAuthDataSource}
                pagination={{ position: ["bottomCenter"] }}
                loading={props.isFetchingUnused}
              />
            </div>
          )}
        </div>
      )}
      {state === 'auth-detail' && (
        <AuthDetail
          providerList={providerList}
          authDetail={authDetail}
          triggerStartPage={triggerStartPage}
        />
      )}
    </div>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingExpired: store.rootReducer.authExpired.isFetchingExpired, //open Auth table
  isFetchingUnused: store.rootReducer.authUnused.isFetchingUnused,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    getExpired,
    getUnusedAuths,
    downloadMemberDocument,
    updateReadInfo,
    getAuthEnabledProviderList,

    //eligibility info page related
    search,
    getHelpCenterLinks,
  },
  dispatch
);

export default connect(mapStateToProps, mapDispatchToProps)(OpenAuthPage);
